/**
 * Turning a delimited table into break sheet drafts.
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
 *
 * A hundred prepared sheets means a hundred document numbers, and those numbers
 * already exist somewhere — in the shipping system, in the export the department
 * sends over, in the spreadsheet the clerk is reading from while typing them in
 * again. Typing them a second time is where the hour goes, and where the typos
 * come from. So the table is the input, and one row becomes one sheet.
 *
 * Everything here is pure: a string in, drafts out. That keeps the parsing —
 * which is the part with the edge cases — testable without a window, and it
 * keeps an imported file, which is untrusted input, from reaching anything that
 * can act on it.
 */

import {
  fieldValueKey,
  MAX_FIELD_VALUE_LENGTH,
  type DraftSheet,
  type WithholdableField,
} from './break-sheet-draft';

/** A row the quoting rule could not resolve. Reported, never merged or dropped in silence. */
export type UnparsedRow = {
  /** The line the row starts on, as the operator counts lines in their file. */
  readonly line: number;
  /**
   * How many rows of the file this one entry accounts for.
   *
   * An unclosed quote does not lose one row, it loses every row from the quote to
   * the end of the file — they are all inside the value it opened. Reporting the
   * entries rather than the rows told an operator that "1 row could not be read"
   * when four had gone, which reads as a rounding error rather than as most of
   * the import.
   */
  readonly rows: number;
  readonly reason: string;
};

export type DelimitedTable = {
  /** The character rows are split on, and a name for it fit to show an operator. */
  readonly delimiter: string;
  readonly delimiterName: string;
  readonly columns: readonly string[];
  readonly rows: readonly (readonly string[])[];
  /**
   * The line each kept row starts on, as the operator counts lines in their file.
   *
   * Parallel to `rows`, and not the same as its index: blank records are skipped
   * and a quoted cell can hold as many newlines as an address has. Every warning
   * that names a row names it from here, because a position in an array is not
   * something an operator can find in a spreadsheet.
   */
  readonly rowLines: readonly number[];
  /** Rows beyond `maxRows`, dropped rather than silently accepted. */
  readonly droppedRows: number;
  readonly unparsedRows: readonly UnparsedRow[];
  /**
   * Rows that did not carry as many values as the heading has columns.
   *
   * Short rows are padded and long ones are cut, so that one ragged row cannot shift
   * the whole import — but neither repair is free and nothing counted them. A header
   * carrying an unquoted delimiter is the ordinary way it happens: a shipping export
   * whose first column is named `Ship To, Name` gives the header four values and every
   * row three, so `CROWN CITY,112-8286526,4` came back with the PRO number sitting
   * under `Name`, pre-mapped by `suggestMapping`, with `droppedRows` at 0 and
   * `unparsedRows` empty. The mirror case throws the last cell of every row away just
   * as quietly.
   */
  readonly raggedRows: number;
  /** The line the first ragged row starts on, so the operator has somewhere to look. */
  readonly firstRaggedLine: number | null;
};

/** How many rows one unparsed entry accounts for, counted in non-blank lines. */
const swallowedRows = (text: string, start: number, end: number): number =>
  text
    .slice(start, end)
    .split('\n')
    .filter((line) => line.trim().length > 0).length;

const DELIMITERS: readonly { readonly character: string; readonly name: string }[] = [
  { character: '\t', name: 'tab' },
  { character: ',', name: 'comma' },
  { character: ';', name: 'semicolon' },
  { character: '|', name: 'pipe' },
];

/**
 * One record read out of the file, and where it came from.
 *
 * A record is not a line. A newline inside a quoted cell belongs to the cell —
 * an address field wraps that way routinely — so the file is read record by
 * record with the quoting rule applied once, rather than split into lines by one
 * rule and into cells by another.
 *
 * That divergence is what this replaces, and it deleted data. Line splitting
 * used to toggle quoted mode on *every* quote wherever it appeared and knew
 * nothing of the `""` escape, while cell splitting only entered quoted mode at
 * the start of a cell. A single inch mark — `12" steel pipe`, `6'2"` — put the
 * two halves out of step: the line splitter swallowed newlines until the next
 * quote somewhere further down the file, and the rows in between vanished into
 * one cell with a newline in it that then reached a printed sheet. Nothing
 * counted them, so nothing warned.
 */
type TableRecord = {
  readonly cells: string[];
  /** 1-based, counted in physical lines, which is what the operator sees. */
  readonly line: number;
  /** True when the record opened a quoted value the file never closes. */
  readonly unterminated: boolean;
  /** Whether the source text of this record was blank. */
  readonly blank: boolean;
  readonly next: number;
  readonly nextLine: number;
};

/**
 * Read one record, honouring RFC 4180 quoting.
 *
 * Quoting matters more here than it usually does: a consignee name with a comma
 * in it is the normal case in the tables these sheets are prepared from, and a
 * parser that split it would push every later column one place left and print a
 * hundred sheets with the wrong values on them. A quote is only a quote at the
 * start of a cell; anywhere else it is the character an inch is measured in.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
const readRecord = (text: string, start: number, delimiter: string, line: number): TableRecord => {
  const cells: string[] = [];
  let cell = '';
  let quoted = false;
  let lineBreaks = 0;
  let index = start;
  for (; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
        continue;
      }
      if (character === '\r' || character === '\n') {
        // A wrapped cell keeps its break, in the one form the sheet prints.
        if (character === '\r' && text[index + 1] === '\n') index += 1;
        cell += '\n';
        lineBreaks += 1;
        continue;
      }
      cell += character;
      continue;
    }
    if (character === '"' && cell.trim().length === 0) {
      quoted = true;
      cell = '';
      continue;
    }
    if (character === delimiter) {
      cells.push(cell.trim());
      cell = '';
      continue;
    }
    if (character === '\r' || character === '\n') {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      index += 1;
      lineBreaks += 1;
      break;
    }
    cell += character;
  }
  cells.push(cell.trim());
  return {
    cells,
    line,
    unterminated: quoted,
    blank: cells.every((entry) => entry.length === 0),
    next: index,
    nextLine: line + lineBreaks,
  };
};

/**
 * Read the whole file into records. Blank records are skipped, because a trailing
 * newline is not a row; a record that never closes its quote is set aside to be
 * reported rather than handed on as data.
 */
const readRecords = (
  text: string,
  delimiter: string,
): { rows: TableRecord[]; unparsed: UnparsedRow[] } => {
  const rows: TableRecord[] = [];
  const unparsed: UnparsedRow[] = [];
  let index = 0;
  let line = 1;
  while (index < text.length) {
    const record = readRecord(text, index, delimiter, line);
    if (record.unterminated) {
      unparsed.push({
        line: record.line,
        rows: swallowedRows(text, index, record.next),
        reason: `line ${record.line} opens a quoted value that is never closed`,
      });
    } else if (!record.blank) {
      rows.push(record);
    }
    index = record.next;
    line = record.nextLine;
  }
  return { rows, unparsed };
};

/** How many records the choice is made on, so choosing how to read a file is not reading it four times. */
const RECORDS_SNIFFED = 50;

/**
 * Pick the delimiter by reading the file with each candidate and taking the first
 * that carves *every* record into the same number of values.
 *
 * Reading the header alone and taking the most columns is not enough, and the
 * engine's own reader had the same hole: a tab-separated export whose address column
 * carries a postcode — `14 Mill Rd, Leeds, YO, LS1 4AB` — has three commas against
 * two tabs on every line, so the comma wins outright and `Postcode` and `Vendor Code`
 * come back as one heading. What separates a delimiter from a character that merely
 * appears in the values is that it gives every line the same count. A comma inside a
 * value is ordinary and a tab inside one is not, so where both read the file evenly
 * the earlier candidate wins — which is the order below.
 *
 * A file no candidate reads evenly falls back to the header count, which is the only
 * thing left to go on.
 */
const chooseDelimiter = (text: string): { character: string; name: string } => {
  let best = DELIMITERS[1];
  let bestColumns = 1;
  for (const candidate of DELIMITERS) {
    // Each candidate finds its own records, because where one ends depends on the
    // delimiter too — a newline inside a quoted cell is not the end of anything.
    const counts: number[] = [];
    let index = 0;
    let line = 1;
    while (index < text.length && counts.length < RECORDS_SNIFFED) {
      const record = readRecord(text, index, candidate.character, line);
      index = record.next;
      line = record.nextLine;
      if (record.unterminated) break;
      if (!record.blank) counts.push(record.cells.length);
    }
    const columns = counts[0] ?? 1;
    if (columns >= 2 && counts.every((count) => count === columns)) return candidate;
    if (columns > bestColumns) {
      best = candidate;
      bestColumns = columns;
    }
  }
  return best;
};

/** Header names an operator can pick from a list: never blank, never repeated. */
const nameColumns = (header: readonly string[]): string[] => {
  const used = new Set<string>();
  return header.map((raw, index) => {
    const base = raw.replace(/^﻿/, '').trim() || `Column ${index + 1}`;
    let name = base;
    let suffix = 2;
    while (used.has(name.toLowerCase())) {
      name = `${base} (${suffix})`;
      suffix += 1;
    }
    used.add(name.toLowerCase());
    return name;
  });
};

export type TableParseResult =
  | { readonly ok: true; readonly table: DelimitedTable }
  | { readonly ok: false; readonly reason: string };

/**
 * `maxRows` is the number of sheets one print run can hold. Reading further and
 * then refusing at print time would waste the operator's mapping work, so the
 * excess is dropped here and reported.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export const parseDelimitedTable = (text: string, maxRows: number): TableParseResult => {
  const withoutBom = text.replace(/^﻿/, '');
  const delimiter = chooseDelimiter(withoutBom);
  const { rows: records, unparsed } = readRecords(withoutBom, delimiter.character);

  if (records.length === 0) {
    // An unreadable header is a quoting problem, not an empty file, and saying
    // so is the difference between a fixable file and a mysterious one.
    if (unparsed.length > 0) {
      return {
        ok: false,
        reason: `That file could not be read: ${unparsed[0].reason}. Close the quote, or remove it, and try again.`,
      };
    }
    return { ok: false, reason: 'That file is empty.' };
  }
  if (records.length === 1 && unparsed.length === 0) {
    return {
      ok: false,
      reason:
        'That file has a header row and no data rows, so there is nothing to make sheets from.',
    };
  }

  const columns = nameColumns(records[0].cells);
  if (columns.length < 1) return { ok: false, reason: 'That file has no columns.' };

  const body = records.slice(1);
  const kept = body.slice(0, maxRows);
  // Short rows are padded and long rows are cut, so a column index always
  // resolves and one ragged row cannot shift the whole import. Both repairs are
  // counted: a row that does not line up with the heading is a row whose values
  // are under headings they were never exported under, and silence about that is
  // how a hundred sheets print a column nobody chose.
  const rows = kept.map((record) => columns.map((_, index) => record.cells[index] ?? ''));
  const ragged = kept.filter((record) => record.cells.length !== columns.length);

  return {
    ok: true,
    table: {
      delimiter: delimiter.character,
      delimiterName: delimiter.name,
      columns,
      rows,
      rowLines: kept.map((record) => record.line),
      droppedRows: body.length - kept.length,
      unparsedRows: unparsed,
      raggedRows: ragged.length,
      firstRaggedLine: ragged.length > 0 ? ragged[0].line : null,
    },
  };
};

const normalize = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The shortest run of characters allowed to stand as evidence that two names are
 * the same field.
 *
 * The guard used to be on the length of the *column* name, which is the side that
 * is never in doubt: `suggestColumn("PO", ["Position", "Purchase Order"])`
 * pre-filled Position, and `suggestColumn("No", ["Notes", "Number"])` pre-filled
 * Notes, because "Position" and "Notes" are long. What has to be long enough to
 * mean something is the part the two names share, and two letters never is —
 * this pre-fills a mapping an operator accepts at a glance, and a hundred sheets
 * then print a column nobody chose.
 */
const MIN_SHARED_CHARACTERS = 3;

/**
 * Guess which column holds a field, so the common case — a table exported from
 * the system the fields were named after — needs no mapping at all. Exact match
 * first, then containment either way, so "Invoice Number" finds "invoice_no"
 * only after nothing better exists. A field with no name long enough to be
 * recognised by is left unmapped rather than guessed at.
 */
export const suggestColumn = (fieldName: string, columns: readonly string[]): number => {
  const wanted = normalize(fieldName);
  if (wanted.length === 0) return -1;
  const normalized = columns.map(normalize);
  const exact = normalized.indexOf(wanted);
  if (exact >= 0) return exact;
  return normalized.findIndex(
    (column) =>
      (wanted.length >= MIN_SHARED_CHARACTERS && column.includes(wanted)) ||
      (column.length >= MIN_SHARED_CHARACTERS && wanted.includes(column)),
  );
};

export type ImportDocumentType = {
  readonly id: string;
  readonly name: string;
  /**
   * The fields that can be written on a sheet, with the ids their values are
   * filed under. Names alone were enough while the draft was keyed by name; a
   * draft keyed by field id needs the id at the moment the row becomes a sheet.
   */
  readonly fields: readonly WithholdableField[];
};

export type ImportMapping = {
  /**
   * Either every row makes a sheet of one chosen type, or a column names the
   * type per row. The second is what an export covering several kinds of
   * document looks like.
   */
  readonly typeSource:
    | { readonly kind: 'fixed'; readonly documentTypeId: string }
    | { readonly kind: 'column'; readonly column: number };
  /** Field name to column index. A field absent from the map is left blank. */
  readonly columns: Readonly<Record<string, number>>;
};

export type ImportPlan = {
  readonly sheets: DraftSheet[];
  /** Row numbers, as the operator sees them in the file, whose type was not recognised. */
  readonly unrecognisedTypeRows: readonly { readonly row: number; readonly value: string }[];
  /** Row numbers that mapped to a sheet with nothing on it. */
  readonly emptyRows: readonly number[];
  /**
   * How many values were longer than a break sheet field holds and were cut.
   * A free-text column is the usual source, and the operator is told rather than
   * left to discover it on the paper.
   */
  readonly shortenedValues: number;
};

/** Auto-map every printable field of every eligible type onto a column. */
export const suggestMapping = (
  columns: readonly string[],
  types: readonly ImportDocumentType[],
): Record<string, number> => {
  const mapping: Record<string, number> = {};
  for (const type of types) {
    for (const field of type.fields) {
      if (field.name in mapping) continue;
      const column = suggestColumn(field.name, columns);
      if (column >= 0) mapping[field.name] = column;
    }
  }
  return mapping;
};

/**
 * Build the sheets a table and a mapping would produce.
 *
 * The same function backs the preview and the commit, so what the operator is
 * shown before agreeing is the thing that gets made — not a second rendering of
 * the same intent that can drift from it.
 */
export const buildImportPlan = (
  table: DelimitedTable,
  mapping: ImportMapping,
  types: readonly ImportDocumentType[],
): ImportPlan => {
  const byName = new Map(types.map((type) => [normalize(type.name), type]));
  const byId = new Map(types.map((type) => [type.id, type]));
  const sheets: DraftSheet[] = [];
  const unrecognisedTypeRows: { row: number; value: string }[] = [];
  const emptyRows: number[] = [];
  let shortenedValues = 0;

  table.rows.forEach((cells, index) => {
    // The line the row starts on, which the reader kept for exactly this. Counting
    // "header plus one" instead was wrong for every file with a blank line or a
    // wrapped cell in it — and the unparsed-row warning beside these already used
    // the real line, so an operator was shown one right number and one wrong one
    // in the same panel.
    const rowNumber = table.rowLines[index] ?? index + 2;
    let type: ImportDocumentType | undefined;
    if (mapping.typeSource.kind === 'fixed') {
      type = byId.get(mapping.typeSource.documentTypeId);
    } else {
      const raw = cells[mapping.typeSource.column] ?? '';
      type = byName.get(normalize(raw));
      if (!type) {
        unrecognisedTypeRows.push({ row: rowNumber, value: raw });
        return;
      }
    }
    if (!type) return;

    const values: Record<string, string> = {};
    for (const field of type.fields) {
      // Mapped by name, because a name is what the operator picked a column
      // against and what two types sharing a field have in common; stored by key,
      // because that is what survives the job renaming the field afterwards.
      const column = mapping.columns[field.name];
      if (column === undefined || column < 0) continue;
      const value = (cells[column] ?? '').trim();
      if (value.length === 0) continue;
      if (value.length > MAX_FIELD_VALUE_LENGTH) shortenedValues += 1;
      values[fieldValueKey(field)] = value.slice(0, MAX_FIELD_VALUE_LENGTH);
    }
    if (Object.keys(values).length === 0) emptyRows.push(rowNumber);
    sheets.push({ documentTypeId: type.id, values });
  });

  return { sheets, unrecognisedTypeRows, emptyRows, shortenedValues };
};
